import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../axiosConfig";
import GridCell from "./GridCell";
import Tooltip from "./Tooltip";
import {
  MAX_DISPLAYED_ROWS,
  MAX_SELECTED_PERIODS,
  applyChange,
  change,
  changeDirection,
  formatChange,
  formatValue,
  isBps,
  parseFormat,
  periodHeading,
  roundChange,
  selectionLabels,
} from "./financialFormat";
import "./FinancialsGrid.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// How close (px) the cursor must be to a collapsed column to show its toggle
const HOVER_RADIUS = 16;
// Period columns share the free width between these; below the minimum the table scrolls
const PERIOD_MIN_WIDTH = 110;
const PERIOD_MAX_WIDTH = 160;

const pad = (n) => String(n).padStart(2, "0");
const lastDayOfMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

// "2025-12-31" + 12 months -> "2026-12-31" (month ends stay month ends)
const addMonths = (iso, months) => {
  const [y, m, d] = iso.split("-").map(Number);
  const total = m - 1 + months;
  const year = y + Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  const day =
    d === lastDayOfMonth(y, m - 1)
      ? lastDayOfMonth(year, month)
      : Math.min(d, lastDayOfMonth(year, month));
  return `${year}-${pad(month + 1)}-${pad(day)}`;
};

// The 1st of the company's next year_end month ("Dec", "Mar", ...), counting this
// month until it ends; December if unknown
const nextYearEnd = (yearEnd) => {
  const index = MONTHS.indexOf((yearEnd || "").trim().slice(0, 3));
  const month = index === -1 ? 11 : index;
  const today = new Date();
  let year = today.getFullYear();
  if (new Date(year, month, lastDayOfMonth(year, month)) < today) year += 1;
  return `${year}-${pad(month + 1)}-01`;
};

// "2026-04-01" -> "Apr 2026"
const formatEndDate = (iso) => {
  const [y, m] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};

// Years offered for a period's end date: 5 either side of this year, plus the
// period's own year if it's outside that
const YEAR_RANGE = 5;
const endDateYears = (currentYear) => {
  const thisYear = new Date().getFullYear();
  const years = [];
  for (let y = thisYear - YEAR_RANGE; y <= thisYear + YEAR_RANGE; y += 1) years.push(y);
  if (!years.includes(currentYear)) years.push(currentYear);
  return years.sort((a, b) => a - b);
};

// Server table -> editable draft. Periods are keyed by period_id, or "new-N" until saved.
const fromTable = (table) => ({
  rows: (table?.rows ?? []).map(({ financial_id, display }) => ({ financial_id, display })),
  periods: (table?.periods ?? []).map((p) => ({
    key: String(p.period_id),
    period_id: p.period_id,
    end_date: p.end_date,
    duration: p.duration,
    name: p.name ?? "",
    value_type: p.value_type,
    selected: p.selected,
  })),
  values: Object.fromEntries(
    Object.entries(table?.values ?? {}).map(([periodId, values]) => [periodId, { ...values }]),
  ),
});

// Draft -> save payload; also compared with the loaded table to tell whether anything changed
const toPayload = (draft) => ({
  rows: draft.rows,
  periods: draft.periods,
  values: draft.periods.flatMap((p) =>
    draft.rows
      .map((r) => ({
        period_key: p.key,
        financial_id: r.financial_id,
        value: draft.values[p.key]?.[r.financial_id] ?? null,
      }))
      .filter((v) => v.value !== null),
  ),
});

// Move a period to the end of the periods sharing its end_date, keeping end_date order
const placeByDate = (periods, key) => {
  const period = periods.find((p) => p.key === key);
  const rest = periods.filter((p) => p.key !== key);
  let index = rest.findIndex((p) => p.end_date > period.end_date);
  if (index === -1) index = rest.length;
  rest.splice(index, 0, period);
  return rest;
};

const affixes = (fmt) => {
  try {
    const { prefix, suffix } = parseFormat(fmt);
    return { prefix, suffix };
  } catch {
    return { prefix: "", suffix: "" };
  }
};

// As on the note ({value:m} rows in millions); the raw value only shows while editing
const safeFormatValue = (value, fmt, rowValues) => {
  try {
    return formatValue(value, fmt, { rowValues });
  } catch {
    return value === null ? "-" : String(value);
  }
};

const ChevronsIcon = ({ direction }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      d={direction === "left" ? "M8 3 3 8l5 5M13 3 8 8l5 5" : "M3 3l5 5-5 5M8 3l5 5-5 5"}
    />
  </svg>
);

const PencilIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M11.9 1.4a1.6 1.6 0 0 1 2.2 0l.5.5a1.6 1.6 0 0 1 0 2.2L5.4 13.3 1.5 14.5l1.2-3.9 9.2-9.2z"
    />
  </svg>
);

// Box floating above `anchor` (below it if there's no room), following it as the
// table scrolls. Clicking outside it or Escape calls onClose.
const Popover = ({ anchor, onClose, className = "", children }) => {
  const ref = useRef(null);
  const [position, setPosition] = useState(null);

  const place = useCallback(() => {
    if (!anchor || !ref.current) return;
    const a = anchor.getBoundingClientRect();
    const { width, height } = ref.current.getBoundingClientRect();
    const center = a.left + a.width / 2;
    const left = Math.min(Math.max(center - width / 2, 8), window.innerWidth - width - 8);
    const above = a.top - height - 10 >= 8;
    const next = {
      left,
      top: above ? a.top - height - 10 : a.bottom + 10,
      above,
      arrow: Math.min(Math.max(center - left, 14), width - 14),
    };
    setPosition((prev) =>
      prev && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next,
    );
  }, [anchor]);

  // After every render: the content or the column may have moved
  useLayoutEffect(() => place());

  useEffect(() => {
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [place]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (!ref.current?.contains(e.target) && !anchor?.contains(e.target)) onClose();
    };
    const handleKeyDown = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={ref}
      className={`popover ${position?.above === false ? "below" : "above"} ${className}`}
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden",
        "--arrow-x": `${position?.arrow ?? 0}px`,
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

const collapsedStorageKey = (companyId) => `financials_collapsed_${companyId}`;

const loadCollapsed = (companyId) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(collapsedStorageKey(companyId)) || "[]"));
  } catch {
    return new Set();
  }
};

// Editable financials table of a Benchmark company: rows are financials, columns
// are periods, plus a CHG column before the 2nd and 4th selected periods.
const FinancialsGrid = ({ companyId, table, financials = [], yearEnd, onSaved, onDirtyChange }) => {
  const [initial, setInitial] = useState(() => fromTable(table));
  const [draft, setDraft] = useState(initial);
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingPeriodKey, setEditingPeriodKey] = useState(null);
  const [collapsed, setCollapsed] = useState(() => loadCollapsed(companyId));
  const [hoverKey, setHoverKey] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [periodWidth, setPeriodWidth] = useState(PERIOD_MAX_WIDTH);
  const dragging = useRef(null);
  const newPeriodCount = useRef(0);
  const headerRefs = useRef({});
  const scrollRef = useRef(null);
  const tableRef = useRef(null);

  const dirty = useMemo(
    () => JSON.stringify(toPayload(draft)) !== JSON.stringify(toPayload(initial)),
    [draft, initial],
  );
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Pick up refreshed data (the companies list reloads every minute) unless
  // there are unsaved changes
  useEffect(() => {
    if (dirtyRef.current) return;
    const fresh = fromTable(table);
    setInitial(fresh);
    setDraft(fresh);
  }, [table]);

  // Clicking anywhere off the table unselects the cell
  useEffect(() => {
    if (!selectedCell) return;
    const handleMouseDown = (e) => {
      if (!tableRef.current?.contains(e.target)) setSelectedCell(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [selectedCell]);

  // Name, format and bigger_is_better of each financial; the catalogue has the latest
  const info = useMemo(() => {
    const byId = {};
    (table?.rows ?? []).forEach((r) => (byId[r.financial_id] = r));
    financials.forEach((f) => (byId[f.financial_id] = { ...byId[f.financial_id], ...f }));
    return byId;
  }, [table, financials]);

  const labels = selectionLabels(draft.periods);
  const selectedKeys = draft.periods.filter((p) => p.selected).map((p) => p.key);
  const displayedCount = draft.rows.filter((r) => r.display).length;

  // Period columns, with CHG immediately before the 2nd and 4th selected periods
  const columns = [];
  draft.periods.forEach((period) => {
    const position = selectedKeys.indexOf(period.key);
    if (position === 1 || position === 3) {
      columns.push({
        type: "chg",
        key: `chg-${period.key}`,
        oldKey: selectedKeys[position - 1],
        newKey: period.key,
      });
    }
    columns.push({ type: "period", key: period.key, period });
  });

  const isCollapsed = (column) => column.type === "period" && collapsed.has(column.key);

  const valueOf = (periodKey, financialId) => draft.values[periodKey]?.[financialId] ?? null;

  // --- Edits ---

  const setValue = (periodKey, financialId, value) =>
    setDraft((d) => ({
      ...d,
      values: { ...d.values, [periodKey]: { ...d.values[periodKey], [financialId]: value } },
    }));

  const updatePeriod = (key, changes) =>
    setDraft((d) => {
      let periods = d.periods.map((p) => {
        if (p.key !== key) return p;
        const updated = { ...p, ...changes };
        if (updated.value_type !== "estimate") updated.selected = false;
        return updated;
      });
      if ("end_date" in changes) periods = placeByDate(periods, key);
      return { ...d, periods };
    });

  const toggleSelected = (key) => {
    const period = draft.periods.find((p) => p.key === key);
    if (!period.selected && selectedKeys.length >= MAX_SELECTED_PERIODS) return;
    updatePeriod(key, { selected: !period.selected });
  };

  const addPeriod = () => {
    const last = draft.periods[draft.periods.length - 1];
    newPeriodCount.current += 1;
    const period = {
      key: `new-${newPeriodCount.current}`,
      period_id: null,
      end_date: last ? addMonths(last.end_date, 12) : nextYearEnd(yearEnd),
      duration: 12,
      name: "",
      value_type: "estimate",
      selected: false,
    };
    setDraft((d) => ({ ...d, periods: placeByDate([...d.periods, period], period.key) }));
    setEditingPeriodKey(period.key);
  };

  const deletePeriod = (key) => {
    const period = draft.periods.find((p) => p.key === key);
    // Only ask when the period has values for the table's financials
    const hasValues = draft.rows.some((r) => valueOf(key, r.financial_id) !== null);
    if (hasValues && !window.confirm(`Delete period ${periodHeading(period, labels[key])} and its values?`)) return;
    setDraft((d) => ({ ...d, periods: d.periods.filter((p) => p.key !== key) }));
    setEditingPeriodKey(null);
    setSelectedCell(null);
  };

  const addRow = (financialId) =>
    setDraft((d) => ({
      ...d,
      rows: [
        ...d.rows,
        { financial_id: financialId, display: displayedCount < MAX_DISPLAYED_ROWS },
      ],
    }));

  const removeRow = (financialId) => {
    const name = info[financialId]?.name ?? "this financial";
    // Only ask when the row has values in the table's periods
    const hasValues = draft.periods.some((p) => valueOf(p.key, financialId) !== null);
    if (hasValues && !window.confirm(`Remove ${name} from this table? Its values are kept and come back if it's added again.`)) return;
    setDraft((d) => ({ ...d, rows: d.rows.filter((r) => r.financial_id !== financialId) }));
    setSelectedCell(null);
  };

  const toggleDisplay = (financialId) =>
    setDraft((d) => ({
      ...d,
      rows: d.rows.map((r) => (r.financial_id === financialId ? { ...r, display: !r.display } : r)),
    }));

  const toggleCollapsed = (key) => {
    const next = new Set(collapsed);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setCollapsed(next);
    setHoverKey(null);
    if (key === editingPeriodKey) setEditingPeriodKey(null);
    try {
      localStorage.setItem(collapsedStorageKey(companyId), JSON.stringify([...next]));
    } catch {
      // Not remembered; fine
    }
  };

  // --- Keyboard navigation (skips collapsed columns) ---

  const moveSelection = (dRow, dCol) =>
    setSelectedCell((cell) => {
      if (!cell) return cell;
      const row = Math.min(Math.max(cell.row + dRow, 0), draft.rows.length - 1);
      let col = cell.col;
      if (dCol) {
        for (let c = cell.col + dCol; c >= 0 && c < columns.length; c += dCol) {
          if (!isCollapsed(columns[c])) {
            col = c;
            break;
          }
        }
      }
      return { row, col };
    });

  // --- Drag and drop (rows by their handle, columns by their header handle) ---

  const startDrag = (e, kind, key) => {
    dragging.current = { kind, key };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(key));
  };

  const dragOver = (e, kind, key) => {
    if (dragging.current?.kind !== kind || dragging.current.key === key) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const after =
      kind === "row"
        ? e.clientY > rect.top + rect.height / 2
        : e.clientX > rect.left + rect.width / 2;
    if (dropTarget?.key !== key || dropTarget?.after !== after || dropTarget?.kind !== kind) {
      setDropTarget({ kind, key, after });
    }
  };

  const endDrag = () => {
    dragging.current = null;
    setDropTarget(null);
  };

  const drop = (e) => {
    e.preventDefault();
    const source = dragging.current;
    const target = dropTarget;
    endDrag();
    if (!source || !target || source.kind !== target.kind || source.key === target.key) return;

    if (source.kind === "row") {
      setDraft((d) => {
        const moved = d.rows.find((r) => r.financial_id === source.key);
        const rows = d.rows.filter((r) => r.financial_id !== source.key);
        const index = rows.findIndex((r) => r.financial_id === target.key) + (target.after ? 1 : 0);
        rows.splice(index, 0, moved);
        return { ...d, rows };
      });
    } else {
      // The column takes the end_date of the column it's dropped on
      setDraft((d) => {
        const moved = d.periods.find((p) => p.key === source.key);
        const targetPeriod = d.periods.find((p) => p.key === target.key);
        const periods = d.periods.filter((p) => p.key !== source.key);
        const index = periods.indexOf(targetPeriod) + (target.after ? 1 : 0);
        periods.splice(index, 0, { ...moved, end_date: targetPeriod.end_date });
        return { ...d, periods };
      });
    }
    setSelectedCell(null);
  };

  const dropClass = (kind, key) =>
    dropTarget?.kind === kind && dropTarget.key === key
      ? dropTarget.after
        ? "drop-after"
        : "drop-before"
      : "";

  // Show a collapsed column's toggle when the cursor is over or near it
  const handleMouseMove = (e) => {
    let found = null;
    for (const key of collapsed) {
      const rect = headerRefs.current[key]?.getBoundingClientRect();
      if (rect && e.clientX >= rect.left - HOVER_RADIUS && e.clientX <= rect.right + HOVER_RADIUS) {
        found = key;
        break;
      }
    }
    if (found !== hoverKey) setHoverKey(found);
  };

  // Width of each period column: the width left over by the other columns, split
  // between the periods and kept between PERIOD_MIN_WIDTH and PERIOD_MAX_WIDTH.
  // Anything left after that goes to the "+ Period" column.
  const fitPeriods = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const cells = [...container.querySelectorAll(".header-row > th")];
    const periodCells = cells.filter(
      (c) => c.classList.contains("period-header") && !c.classList.contains("collapsed"),
    );
    if (!periodCells.length) return;
    const others = cells
      .filter((c) => !periodCells.includes(c) && !c.classList.contains("add-period-header"))
      .reduce((sum, c) => sum + c.offsetWidth, 0);
    // The "+ Period" button and its cell's padding and border
    const addButton = container.querySelector(".add-period-header button");
    const addColumn = addButton ? addButton.offsetWidth + 17 : 0;
    const free = container.clientWidth - others - addColumn;
    const width = Math.min(
      PERIOD_MAX_WIDTH,
      Math.max(PERIOD_MIN_WIDTH, Math.floor(free / periodCells.length)),
    );
    setPeriodWidth((w) => (w === width ? w : width));
  }, []);

  // After every render (columns come and go), and whenever the modal resizes
  useLayoutEffect(() => fitPeriods());

  const hasTable = Boolean(table);
  useEffect(() => {
    if (!hasTable || !scrollRef.current) return;
    const observer = new ResizeObserver(fitPeriods);
    observer.observe(scrollRef.current);
    return () => observer.disconnect();
  }, [fitPeriods, hasTable]);

  // --- Save ---

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await axios.post(
        "/api/financials/save/",
        { company_id: companyId, ...toPayload(draft) },
        { withCredentials: true },
      );
      const fresh = fromTable(response.data.financials_table);
      setInitial(fresh);
      setDraft(fresh);
      setSelectedCell(null);
      setEditingPeriodKey(null);
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save the financials table.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraft(initial);
    setSelectedCell(null);
    setEditingPeriodKey(null);
    setError("");
  };

  if (!table) {
    return <p className="financials-unavailable">The financials table couldn't be loaded.</p>;
  }

  const editingPeriod = draft.periods.find((p) => p.key === editingPeriodKey);
  const availableFinancials = financials
    .filter((f) => !draft.rows.some((r) => r.financial_id === f.financial_id))
    .sort((a, b) => a.name.localeCompare(b.name));

  // --- Rendering ---

  const renderPeriodHeader = (column) => {
    const { period, key } = column;
    const collapsedColumn = collapsed.has(key);
    return (
      <th
        key={key}
        ref={(el) => {
          headerRefs.current[key] = el;
        }}
        className={[
          "period-header",
          period.value_type,
          period.selected ? "is-selected" : "",
          collapsedColumn ? "collapsed" : "",
          collapsedColumn && hoverKey === key ? "show-toggle" : "",
          editingPeriodKey === key ? "is-editing" : "",
          dropClass("period", key),
        ].join(" ")}
        onDragOver={(e) => dragOver(e, "period", key)}
        onDrop={drop}
      >
        <div className="period-header-top">
          {!collapsedColumn && (
            <Tooltip text="Drag to move this period (it takes the end date of the column it's dropped on)">
              <span
                className="drag-handle"
                draggable
                onDragStart={(e) => startDrag(e, "period", key)}
                onDragEnd={endDrag}
              >
                ⠿
              </span>
            </Tooltip>
          )}
          <span className="period-header-buttons">
            {!collapsedColumn && (
              <Tooltip text="Edit period">
                <button
                  className="edit-period"
                  onClick={() => setEditingPeriodKey(editingPeriodKey === key ? null : key)}
                  aria-label="Edit period"
                >
                  <PencilIcon />
                </button>
              </Tooltip>
            )}
            <Tooltip text={collapsedColumn ? "Expand column" : "Collapse column"}>
              <button
                className="collapse-toggle"
                onClick={() => toggleCollapsed(key)}
                aria-label={collapsedColumn ? "Expand column" : "Collapse column"}
              >
                <ChevronsIcon direction={collapsedColumn ? "right" : "left"} />
              </button>
            </Tooltip>
          </span>
        </div>
        {!collapsedColumn && (
          <>
            <div className="period-heading">{periodHeading(period, labels[key])}</div>
            <div className="period-meta">
              {formatEndDate(period.end_date)} · {period.duration ?? "?"}m
            </div>
            {period.value_type === "estimate" ? (
              <label className="period-select" title="Use this period in the note">
                <input
                  type="checkbox"
                  checked={period.selected}
                  disabled={!period.selected && selectedKeys.length >= MAX_SELECTED_PERIODS}
                  onChange={() => toggleSelected(key)}
                />
                Note
              </label>
            ) : (
              <div className="period-actual">Actual</div>
            )}
          </>
        )}
      </th>
    );
  };

  const renderCell = (row, rowIndex, column, colIndex) => {
    const financialId = row.financial_id;
    if (isCollapsed(column)) return <td key={column.key} className="collapsed-cell" />;

    const fmt = info[financialId]?.format ?? "{value}";
    const selected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
    const common = {
      selected,
      onSelect: () => setSelectedCell({ row: rowIndex, col: colIndex }),
      onMove: moveSelection,
    };

    if (column.type === "chg") {
      const oldValue = valueOf(column.oldKey, financialId);
      const newValue = valueOf(column.newKey, financialId);
      const chg = change(oldValue, newValue, fmt);
      const direction = changeDirection(oldValue, newValue, info[financialId]?.bigger_is_better);
      return (
        <GridCell
          key={column.key}
          {...common}
          className={`chg-cell ${direction ?? ""}`}
          display={formatChange(chg, fmt)}
          raw={chg === null ? null : roundChange(chg, fmt)}
          suffix={isBps(fmt) ? "bps" : "%"}
          editable={oldValue !== null && (isBps(fmt) || oldValue !== 0)}
          clearable={false}
          onCommit={(value) => setValue(column.newKey, financialId, applyChange(oldValue, value, fmt))}
        />
      );
    }

    const value = valueOf(column.key, financialId);
    return (
      <GridCell
        key={column.key}
        {...common}
        className="period-cell"
        display={safeFormatValue(
          value,
          fmt,
          draft.periods.map((p) => valueOf(p.key, financialId)),
        )}
        raw={value}
        {...affixes(fmt)}
        onCommit={(newValue) => setValue(column.key, financialId, newValue)}
      />
    );
  };

  return (
    <div className="financials-grid">
      <div
        ref={scrollRef}
        className="financials-scroll"
        onMouseMove={collapsed.size ? handleMouseMove : undefined}
        onMouseLeave={() => setHoverKey(null)}
      >
        {/* One row group, so the "+ Period" cell can run down the whole last column */}
        <table ref={tableRef} style={{ "--period-width": `${periodWidth}px` }}>
          <tbody>
            <tr className="header-row">
              <th className="row-label-header">
                Financial
                <span className="display-count" title="Rows shown on the note">
                  {displayedCount}/{MAX_DISPLAYED_ROWS} on note
                </span>
              </th>
              {columns.map((column) =>
                column.type === "chg" ? (
                  <th key={column.key} className="chg-header">
                    CHG
                  </th>
                ) : (
                  renderPeriodHeader(column)
                ),
              )}
              <th
                className="add-period-header"
                rowSpan={draft.rows.length + (draft.rows.length === 0 ? 1 : 0) + 2}
              >
                <button onClick={addPeriod} title="Add a period">
                  + Period
                </button>
              </th>
            </tr>
            {draft.rows.map((row, rowIndex) => {
              const financial = info[row.financial_id] ?? {};
              return (
                <tr
                  key={row.financial_id}
                  className={dropClass("row", row.financial_id)}
                  onDragOver={(e) => dragOver(e, "row", row.financial_id)}
                  onDrop={drop}
                >
                  <td className="row-label">
                    <div className="row-label-inner">
                      <span
                        className="drag-handle"
                        draggable
                        onDragStart={(e) => startDrag(e, "row", row.financial_id)}
                        onDragEnd={endDrag}
                        title="Drag to reorder"
                      >
                        ⠿
                      </span>
                      <span className="row-name" title={financial.format}>
                        {financial.name ?? `Financial ${row.financial_id}`}
                      </span>
                      <Tooltip
                        text={
                          !row.display && displayedCount >= MAX_DISPLAYED_ROWS
                            ? `At most ${MAX_DISPLAYED_ROWS} rows can be shown on the note`
                            : "Show on the note"
                        }
                      >
                        <label className="display-toggle">
                          <input
                            type="checkbox"
                            checked={row.display}
                            disabled={!row.display && displayedCount >= MAX_DISPLAYED_ROWS}
                            onChange={() => toggleDisplay(row.financial_id)}
                          />
                          <span className="switch" />
                        </label>
                      </Tooltip>
                      <button
                        className="remove-row"
                        onClick={() => removeRow(row.financial_id)}
                        title="Remove from this table"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                  {columns.map((column, colIndex) => renderCell(row, rowIndex, column, colIndex))}
                </tr>
              );
            })}
            {draft.rows.length === 0 && (
              <tr>
                <td className="empty-row" colSpan={columns.length + 1}>
                  No financials selected - add one below
                </td>
              </tr>
            )}
            <tr className="add-row">
              <td className="row-label">
                <select
                  className="add-financial"
                  value=""
                  onChange={(e) => e.target.value && addRow(Number(e.target.value))}
                  disabled={availableFinancials.length === 0}
                >
                  <option value="">
                    {availableFinancials.length ? "+ Add financial..." : "All financials added"}
                  </option>
                  {availableFinancials.map((f) => (
                    <option key={f.financial_id} value={f.financial_id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </td>
              {columns.length > 0 && <td colSpan={columns.length} />}
            </tr>
          </tbody>
        </table>
      </div>

      {editingPeriod && (
        <Popover
          anchor={headerRefs.current[editingPeriod.key]}
          onClose={() => setEditingPeriodKey(null)}
          className="period-editor"
        >
          <strong>{periodHeading(editingPeriod, labels[editingPeriod.key])}</strong>
          <label>
            Name
            <input
              type="text"
              maxLength={32}
              value={editingPeriod.name}
              placeholder={periodHeading({ ...editingPeriod, name: "" }, labels[editingPeriod.key])}
              onChange={(e) => updatePeriod(editingPeriod.key, { name: e.target.value })}
            />
          </label>
          <label>
            End date
            {(() => {
              // Month and year only; the end date is the 1st of that month
              const [year, month] = editingPeriod.end_date.split("-").map(Number);
              const setEndDate = (y, m) =>
                updatePeriod(editingPeriod.key, { end_date: `${y}-${pad(m)}-01` });
              return (
                <span className="end-date-inputs">
                  <select value={month} onChange={(e) => setEndDate(year, Number(e.target.value))}>
                    {MONTHS.map((name, i) => (
                      <option key={name} value={i + 1}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <select value={year} onChange={(e) => setEndDate(Number(e.target.value), month)}>
                    {endDateYears(year).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </span>
              );
            })()}
          </label>
          <label>
            Months
            <input
              type="number"
              min={1}
              max={120}
              value={editingPeriod.duration ?? ""}
              onChange={(e) =>
                updatePeriod(editingPeriod.key, {
                  duration: e.target.value === "" ? null : Number(e.target.value),
                })
              }
            />
          </label>
          <label>
            Type
            <select
              value={editingPeriod.value_type}
              onChange={(e) => updatePeriod(editingPeriod.key, { value_type: e.target.value })}
            >
              <option value="actual">Actual</option>
              <option value="estimate">Estimate / forecast</option>
            </select>
          </label>
          <div className="period-editor-actions">
            <button className="delete-period" onClick={() => deletePeriod(editingPeriod.key)}>
              Delete period
            </button>
            <button className="cancel-btn" onClick={() => setEditingPeriodKey(null)}>
              Done
            </button>
          </div>
        </Popover>
      )}

      <div className="financials-footer">
        <span className="financials-error">{error}</span>
        <button className="cancel-btn" onClick={handleDiscard} disabled={!dirty || saving}>
          Discard
        </button>
        <button className="confirm-btn" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default FinancialsGrid;
